import { createAsyncThunk } from "@reduxjs/toolkit";
import type { SortRule } from "@/shared/types";
import { sortBasicInformation } from "./logic";
import { generateBasicInformation } from "./mock";
import type { BasicInformationRow } from "./types";

/**
 * Mock data workflow (thunks = workflow, logic.ts = calculation — section 43):
 * no CubeJS backend exists for this dataset, so the thunks simulate the
 * fetch latency of the mock service and return one page of generated rows.
 * Swapping in a real API is a body-only change:
 * - `loadBasicInformation(sort)` — page 0, refetched on every sort change (the sort
 *   value maps to the query's ORDER BY, section 52 server-side sorting);
 * - `loadMoreBasicInformation()` — the next page of the same sorted pool, fired by
 *   the table's `onScrollNearBottom` (reference isFetchingNextPage).
 */
const MOCK_LATENCY_MS = 300;
const MOCK_TOTAL = 2000;
/** Rows per page — mirrors the imported table-config.json page size. */
export const BASIC_INFORMATION_PAGE_SIZE = 100;

/** One fetched page: rows plus the paging metadata the slice stores. */
export interface BasicInformationPage {
  rows: BasicInformationRow[];
  pageIndex: number;
  total: number;
}

// The generator is deterministic (fixed seed), so one cached pool — sorted
// per request — keeps every page consistent with the rows already loaded.
let poolCache: BasicInformationRow[] | null = null;
function basicInformationPool(): BasicInformationRow[] {
  poolCache ??= generateBasicInformation(MOCK_TOTAL);
  return poolCache;
}

function fetchBasicInformationPage(
  pageIndex: number,
  sort: SortRule[],
): Promise<BasicInformationPage> {
  return new Promise((resolve) => {
    setTimeout(() => {
      const sorted = sortBasicInformation(basicInformationPool(), sort);
      const start = pageIndex * BASIC_INFORMATION_PAGE_SIZE;
      resolve({
        rows: sorted.slice(start, start + BASIC_INFORMATION_PAGE_SIZE),
        pageIndex,
        total: MOCK_TOTAL,
      });
    }, MOCK_LATENCY_MS);
  });
}

export const loadBasicInformation = createAsyncThunk<BasicInformationPage, SortRule[]>(
  "basicInformationTable/load",
  async (sort, { signal }) => {
    const page = await fetchBasicInformationPage(0, sort);
    if (signal.aborted) {
      throw new Error("aborted");
    }
    return page;
  },
);

export const loadMoreBasicInformation = createAsyncThunk<BasicInformationPage, void>(
  "basicInformationTable/loadMore",
  async (_arg, { getState, signal }) => {
    // Load-more continues the active sort: pages of one sorted pool.
    const state = getState() as {
      basicInformationTable: { page: number; sort: SortRule[] };
    };
    const { page, sort } = state.basicInformationTable;
    const nextPage = await fetchBasicInformationPage(page + 1, sort);
    if (signal.aborted) {
      throw new Error("aborted");
    }
    return nextPage;
  },
);
